
import { BadgeCheck, Heart, Clock, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CardTitle, CardDescription, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";

interface DealerHeaderProps {
  name: string;
  address: string;
  is_verified: boolean;
  city?: string;
  postal_code?: string;
  phone?: string;
  openingHours?: string;
  isFavorite: boolean;
  isHomePage?: boolean;
  onToggleFavorite: (e: React.MouseEvent) => void;
}

export function DealerHeader({
  name,
  address,
  is_verified,
  city,
  postal_code,
  phone,
  openingHours,
  isFavorite,
  isHomePage = false,
  onToggleFavorite
}: DealerHeaderProps) {
  const HeartIcon = isHomePage ? motion.button : Button;
  const heartMotionProps = isHomePage ? {
    whileTap: { scale: 0.9 },
    whileHover: { scale: 1.1 }
  } : {};
  
  return (
    <CardHeader className={cn(
      "pb-2 relative", 
      isHomePage && "pb-1 bg-gradient-to-r from-metal-light/5 to-transparent border-b border-metal-light/20"
    )}>
      {isHomePage && (
        <div className="absolute top-0 left-0 w-1/3 h-1 bg-gradient-to-r from-gold/80 to-transparent rounded-br-full" />
      )}
      <div className="flex items-center justify-between">
        <CardTitle className={cn(
          "text-xl font-medium flex items-center",
          isHomePage && "text-gradient metal-text text-gold-dark dark:text-gold-light"
        )}>
          {name}
          {is_verified && (
            <BadgeCheck className={cn(
              "h-5 w-5 ml-2", 
              isHomePage ? "text-gold" : "text-metal"
            )} />
          )}
        </CardTitle>
        
        {isHomePage ? (
          <HeartIcon 
            {...heartMotionProps}
            onClick={onToggleFavorite}
            className={cn(
              "flex items-center justify-center p-2 rounded-full transition-colors duration-200",
              isFavorite ? "text-red-500" : "text-muted-foreground hover:text-red-400"
            )}
          >
            <Heart className="h-5 w-5" fill={isFavorite ? "currentColor" : "none"} />
          </HeartIcon>
        ) : (
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onToggleFavorite}
            className={cn(
              "transition-colors duration-200",
              isFavorite ? "text-red-500" : "text-muted-foreground hover:text-red-400"
            )}
          >
            <Heart className="h-5 w-5" fill={isFavorite ? "currentColor" : "none"} />
          </Button>
        )}
      </div>
      <CardDescription className="flex items-center text-foreground/70 flex-wrap gap-1">
        <MapPin className="h-4 w-4 mr-1 text-foreground/70 shrink-0" />
        <span className="truncate">{address}</span>
        {city && (
          <Badge 
            variant="outline" 
            className={cn(
              "ml-auto text-xs",
              isHomePage && "card-badge bg-metal-light/30 text-metal-dark dark:text-metal-light"
            )}
          >
            {city}
          </Badge>
        )}
        {postal_code && (
          <Badge 
            variant="outline" 
            className={cn(
              "text-xs",
              isHomePage && "card-badge bg-metal-light/30 text-metal-dark dark:text-metal-light"
            )}
          >
            {postal_code}
          </Badge>
        )}
      </CardDescription>
      
      {/* Contact information below address */}
      <div className="mt-2 space-y-1">
        {phone && (
          <div className="flex items-center text-sm text-foreground/80">
            <Phone className="h-4 w-4 mr-2 shrink-0 text-metal/70" />
            <a href={`tel:${phone}`} className="truncate hover:text-primary transition-colors">
              {phone}
            </a>
          </div>
        )}
        
        {openingHours && (
          <div className="flex items-center text-sm text-foreground/80">
            <Clock className="h-4 w-4 mr-2 shrink-0 text-metal/70" />
            <span className="truncate">{openingHours}</span>
          </div>
        )}
      </div>
    </CardHeader>
  );
}
